/*
 * Sample customers (P5/P6): the companies that bought. Every customer-side
 * screen — onboarding, support, renewals, churn — refers to these by id so the
 * prototype reads as one company's book of business.
 *
 * lifecycle: onboarding | live | at_risk | exiting | former
 * health:    healthy | needs_attention | at_risk | critical
 */
import { daysFromNow, daysUntil } from "@/helpers/crm";

const C = (id, name, industry, segment, location_id, lifecycle, health, mrr, product_ids, csm_id, am_id, since, renewal, extra = {}) => ({
  id,
  code: `CUS-${String(id + 200).padStart(4, "0")}`,
  name,
  industry,
  segment,
  location_id,
  lifecycle,
  health,
  mrr,
  arr: mrr * 12,
  product_ids,
  csm_id,
  account_manager_id: am_id,
  customer_since: daysFromNow(-since),
  renewal_date: renewal === null ? null : daysFromNow(renewal),
  users: { licensed: { SME: 50, MID: 200, ENT: 1000 }[segment], active: 0 },
  ...extra,
});

export const customers = [
  C(1, "Savannah Hotels Group", "Hospitality", "ENT", 1, "live", "healthy", 180000, [1], 7, 1, 720, 30, { primary_contact: "Linda Mutiso" }),
  C(2, "Jamii Microfinance", "Banking & Finance", "MID", 2, "live", "healthy", 145000, [2, 1], 10, 2, 410, 120, { primary_contact: "Stella Oduya" }),
  C(3, "Coastline Freight", "Logistics", "MID", 3, "at_risk", "critical", 60000, [1], 7, 4, 530, 45, { primary_contact: "Amina Juma" }),
  C(4, "Equator Pharmacies", "Healthcare", "MID", 4, "at_risk", "at_risk", 51500, [1], 10, 5, 300, 45, { primary_contact: "Beatrice Auma" }),
  C(5, "Bidii Manufacturers", "Manufacturing", "MID", 6, "live", "needs_attention", 78000, [1], 7, 1, 380, 30, { primary_contact: "Irene Nduta" }),
  C(6, "Upper Hill Medical Centre", "Healthcare", "MID", 2, "live", "needs_attention", 60000, [1], 10, 2, 260, 200, { primary_contact: "Dr. Paul Kamau" }),
  C(7, "Mwangaza Academy", "Education", "SME", 6, "onboarding", "needs_attention", 25000, [1], 7, 3, 40, 325, { primary_contact: "James Njoroge" }),
  C(8, "Rift Valley Millers", "Manufacturing", "ENT", 5, "onboarding", "healthy", 165000, [1], 10, 2, 25, 340, { primary_contact: "Daniel Rotich" }),
  C(9, "Harambee Sacco", "SACCO", "ENT", 2, "live", "healthy", 210000, [2, 5], 7, 2, 900, 160, { primary_contact: "Philip Kiptoo" }),
  C(10, "Smart Duka Chain", "Retail", "MID", 1, "live", "healthy", 58500, [1], 10, 5, 220, 90, { primary_contact: "Kevin Langat" }),
  C(11, "Kampala Retail Hub", "Retail", "MID", 7, "onboarding", "healthy", 64000, [1], 7, 4, 15, 350, { primary_contact: "Moses Ssali" }),
  C(12, "Tumaini NGO", "NGO", "SME", 5, "exiting", "at_risk", 18000, [1], 10, 3, 610, 20, { primary_contact: "Esther Wairimu" }),
  C(13, "Gatsby Insurance", "Insurance", "MID", 1, "live", "healthy", 78000, [1], 7, 1, 150, 215, { primary_contact: "Martin Gitau" }),
  C(14, "Nyali Beach Resort", "Hospitality", "MID", 3, "former", "critical", 0, [1], 10, 4, 800, null, { primary_contact: "Fatma Said", exited_on: daysFromNow(-60), exit_reason: "CMP-01" }),
  C(15, "Elimu Bora Schools", "Education", "MID", 5, "live", "healthy", 60000, [1], 7, 5, 330, 35, { primary_contact: "Purity Chege" }),
  C(16, "Kijani Solar", "Manufacturing", "SME", 5, "live", "needs_attention", 25000, [1], 10, 4, 190, 60, { primary_contact: "Victor Mutua" }),
  C(17, "Westlands Dental", "Healthcare", "SME", 1, "former", "critical", 0, [1], 7, 3, 500, null, { primary_contact: "Dr. Nancy Mbugua", exited_on: daysFromNow(-120), exit_reason: "BUS-01" }),
  C(18, "Pwani Logistics", "Logistics", "ENT", 3, "live", "healthy", 240000, [1], 10, 4, 1000, 250, { primary_contact: "Hassan Ali" }),
  // Demulla LMS clients: tenants on the LMS platform, supported like any other client.
  C(19, "Gelisa", "Banking & Finance", "MID", 1, "live", "healthy", 130000, [2], 7, 2, 540, 140, { primary_contact: "Joyce Wanjala" }),
  C(20, "Mkopo Bora Credit", "Banking & Finance", "SME", 5, "onboarding", "healthy", 85000, [2], 10, 3, 12, 353, { primary_contact: "Samuel Cheruiyot" }),
  // A standalone client: a bespoke export-tracking system Demulla built and supports for this one customer.
  C(21, "Kilimo Fresh Exporters", "Agriculture", "MID", 1, "live", "healthy", 45000, [4], 7, 1, 300, 65, { primary_contact: "Mary Atieno" }),
];

/*
 * Product accounts: how each customer is known inside Demulla's products.
 * All three products are multi-tenant platforms, so every client is a tenant
 * with a tenant code — DEE014 on DeeHr360 HRM, LMS003 on Demulla LMS — and a
 * sign-in address. A ticket arriving through a product's platform connection
 * names the tenant it came from, and the CRM resolves the customer from this
 * list. A client on both HRM and LMS is one customer with two accounts.
 *
 * Standalone applications (one installation, one client) have no tenant code:
 * the account records the instance address, version and environment, and
 * the app's connection is bound to that client.
 *
 * deployment: platform | standalone
 * status: active | provisioning | suspended | archived
 */
const A = (product_id, tenant_code, address, status, sinceDays) => ({ product_id, deployment: "platform", tenant_code, address, status, since: daysFromNow(-sinceDays) });
const S = (product_id, address, version, environment, status, sinceDays) => ({ product_id, deployment: "standalone", tenant_code: null, address, version, environment, status, since: daysFromNow(-sinceDays) });
const sinceOf = (id) => Math.max(0, -daysUntil(customers.find((x) => x.id === id).customer_since) - 5);
const HRM = (id, slug, status = "active") => A(1, `DEE${String(id).padStart(3, "0")}`, `${slug}.deehr360.com`, status, sinceOf(id));
const LMS = (n, id, slug, status = "active") => A(2, `LMS${String(n).padStart(3, "0")}`, `${slug}.demullalms.com`, status, sinceOf(id));
const ACCOUNTS = {
  1: [HRM(1, "savannahhotels")],
  2: [LMS(2, 2, "jamii"), HRM(2, "jamii")],
  3: [HRM(3, "coastline")],
  4: [HRM(4, "equatorpharm")],
  5: [HRM(5, "bidii")],
  6: [HRM(6, "upperhillmed")],
  7: [HRM(7, "mwangaza")],
  8: [HRM(8, "rvmillers")],
  9: [LMS(1, 9, "harambeesacco"), S(5, "hrm.harambeesacco.com", "v3.8 (on-premise)", "Production", "active", 1400)],
  10: [HRM(10, "smartduka")],
  11: [HRM(11, "kampalaretail", "provisioning")],
  12: [HRM(12, "tumaini")],
  13: [HRM(13, "gatsby")],
  14: [HRM(14, "nyalibeach", "suspended")],
  15: [HRM(15, "elimubora")],
  16: [HRM(16, "kijanisolar"), A(2, "LMS005", "kijanisolar.demullalms.com", "archived", 110)],
  17: [HRM(17, "westlandsdental", "archived")],
  18: [HRM(18, "pwani")],
  // Gelisa: live as tenant LMS003 on the LMS platform; DeeHr360 for its own staff is being set up (ONB-0035).
  19: [LMS(3, 19, "gelisa"), A(1, "DEE019", "gelisa.deehr360.com", "provisioning", 2)],
  // Mkopo Bora: new LMS client; tenant provisioned on the platform, live at go-live.
  20: [A(2, "LMS004", "mkopobora.demullalms.com", "provisioning", 6)],
  21: [S(4, "erp.kilimofresh.co.ke", "2.4.1", "Production", "active", 300), S(4, "uat.erp.kilimofresh.co.ke", "2.5.0-rc1", "UAT", "active", 40)],
};
customers.forEach((c) => (c.product_accounts = ACCOUNTS[c.id] ?? []));

customers.forEach((c, i) => {
  const ratio = { healthy: 0.85, needs_attention: 0.6, at_risk: 0.4, critical: 0.25 }[c.health];
  c.users.active = c.lifecycle === "former" ? 0 : Math.round(c.users.licensed * ratio) - i;
});

export const customerById = (id) => customers.find((c) => c.id === Number(id));
export const customerName = (id) => customerById(id)?.name ?? "—";

// Every product account across customers, with the customer it belongs to.
export const productAccounts = customers.flatMap((c) => c.product_accounts.map((a) => ({ ...a, customer_id: c.id })));
export const accountsFor = (customerId) => customerById(customerId)?.product_accounts ?? [];
export const accountsOfProduct = (productId) => productAccounts.filter((a) => a.product_id === Number(productId));
// Resolve the customer a ticket belongs to from the tenant code it carries (e.g. "DEE014", "LMS003").
export const resolveAccount = (productId, tenantCode) =>
  productAccounts.find((a) => a.product_id === Number(productId) && a.tenant_code && a.tenant_code.toUpperCase() === String(tenantCode ?? "").toUpperCase()) ?? null;
// What identifies an account on screen: the tenant code, or for a standalone install its address.
export const accountKey = (a) => a?.tenant_code ?? a?.address ?? "—";
export const accountStatusTone = (s) => ({ active: "success", provisioning: "info", suspended: "warning", archived: "secondary" })[s] ?? "secondary";

export const lifecycleTone = (l) => ({ onboarding: "info", live: "success", at_risk: "warning", exiting: "danger", former: "secondary" })[l] ?? "secondary";
export const lifecycleLabel = (l) => ({ onboarding: "Onboarding", live: "Live", at_risk: "At risk", exiting: "Exiting", former: "Former customer" })[l] ?? l;
export const healthLabel = (h) => ({ healthy: "Healthy", needs_attention: "Needs attention", at_risk: "At risk", critical: "Critical" })[h] ?? h;
